import React, {FC, useState, useEffect} from 'react'
import {KTSVG} from '../../../../../_metronic/helpers'
import {createPmsPlanLine} from '../../core/pms/_requests'
import {getEquipmentByVessel, getSubcomponentsByEquipment} from '../../core/_requests'
import {getRunningHourCounters} from '../../core/pms/_requests'
import {toast} from 'react-toastify'
import type {
  PmsPlanLineDto,
  PmsWorkType,
  PmsScheduleType,
  PmsIntervalUnit,
  PmsCriticality,
  RunningHourCounterDto,
} from '../../core/pms/_models'
import type {EquipmentDto, SubcomponentDto} from '../../core/_models'
import SearchableSelect from '../../../../shared/components/SearchableSelect'

interface Props {
  visible: boolean
  planId: number
  vesselId: number
  onClose: () => void
  onSuccess: (createdLine?: PmsPlanLineDto) => void
  initialEquipmentId?: number
  initialSubComponentId?: number
}

const LABEL = 'form-label fw-semibold fs-6 mb-2 text-dark'
const WRAP_STYLE: React.CSSProperties = {
  background: 'rgba(15, 23, 42, 0.45)',
  position: 'fixed',
  inset: 0,
  zIndex: 2000,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backdropFilter: 'blur(2px)',
  padding: '24px',
  overflow: 'auto',
  overscrollBehavior: 'contain',
}

// Job Types - single letters only (A-Z)
const JOB_TYPE_WORKTYPE_MAP: Record<string, PmsWorkType> = {
  I: 'INSPECT',
  G: 'CHECK',
  O: 'OVERHAUL',
  C: 'CLEAN',
  A: 'ADJUST',
  R: 'RENEW',
  S: 'CHECK',
  T: 'TEST',
  M: 'CHECK',
  D: 'CHECK',
  L: 'CHECK',
}
const JOB_TYPES: {value: string; label: string; workType: PmsWorkType}[] =
  'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('').map((letter) => ({
    value: letter,
    label: letter,
    workType: JOB_TYPE_WORKTYPE_MAP[letter] ?? 'INSPECT',
  }))

// Helper to map job type letter to work type enum
const getWorkTypeFromJobType = (jobType: string | null | undefined): PmsWorkType => {
  if (!jobType) return 'INSPECT'
  const normalized = jobType.toUpperCase()
  return JOB_TYPE_WORKTYPE_MAP[normalized] ?? 'INSPECT'
}

const SCHEDULE_TYPES: {value: PmsScheduleType; label: string}[] = [
  {value: 'TIME', label: 'Time-based'},
  {value: 'RUNNING_HOURS', label: 'Running Hours'},
  {value: 'HYBRID_WHICHEVER_FIRST', label: 'Hybrid (Whichever First)'},
  {value: 'HYBRID_BOTH_REQUIRED', label: 'Hybrid (Both Required)'},
]

const TIME_PERIODICITY_TYPES: {value: string; label: string}[] = [
  {value: 'M', label: 'Months'},
  {value: 'W', label: 'Weeks'},
  {value: 'D', label: 'Days'},
]
const HOURS_PERIODICITY_TYPES: {value: string; label: string}[] = [{value: 'H', label: 'Hours'}]
const HYBRID_PERIODICITY_TYPES: {value: string; label: string}[] = [
  ...TIME_PERIODICITY_TYPES,
  ...HOURS_PERIODICITY_TYPES,
]

const CRITICALITY_LEVELS: {value: PmsCriticality; label: string}[] = [
  {value: 'LOW', label: 'Low'},
  {value: 'MEDIUM', label: 'Medium'},
  {value: 'HIGH', label: 'High'},
  {value: 'CRITICAL', label: 'Critical'},
]

export const AddPlanLineModal: FC<Props> = ({
  visible,
  planId,
  vesselId,
  onClose,
  onSuccess,
  initialEquipmentId,
  initialSubComponentId,
}) => {
  const [formData, setFormData] = useState<Partial<PmsPlanLineDto>>({
    planId,
    taskDescription: '',
    jobInstructions: '',
    jobType: 'I',
    workType: 'INSPECT',
    scheduleType: 'TIME',
    jobPeriodicity: undefined,
    periodicityId: undefined,
    jobPeriodicity2: undefined,
    periodicityId2: undefined,
    criticality: 'MEDIUM',
    preAlertDays: undefined,
    preAlertHoursThreshold: undefined,
    earlyTolerancePercent: undefined,
    earlyToleranceDays: undefined,
    lateTolerancePercent: undefined,
    lateToleranceDays: undefined,
    lateToleranceHours: undefined,
    active: true,
    componentId: undefined,
    subComponentId: initialSubComponentId,
    runningHourCounterId: undefined,
  })
  const [errors, setErrors] = useState<{[key: string]: string}>({})
  const [submitting, setSubmitting] = useState(false)

  // For hierarchy selection
  const [equipment, setEquipment] = useState<EquipmentDto[]>([])
  const [subcomponents, setSubcomponents] = useState<SubcomponentDto[]>([])
  const [selectedEquipmentId, setSelectedEquipmentId] = useState<number | undefined>(initialEquipmentId)

  // For running hour counters
  const [runningHourCounters, setRunningHourCounters] = useState<RunningHourCounterDto[]>([])
  const [loadingCounters, setLoadingCounters] = useState(false)

  useEffect(() => {
    if (visible && vesselId) {
      loadEquipment()
      // Reset form
      setFormData({
        planId,
        taskDescription: '',
        jobInstructions: '',
        jobType: 'I',
        workType: 'INSPECT',
        scheduleType: 'TIME',
        jobPeriodicity: undefined,
        periodicityId: undefined,
        jobPeriodicity2: undefined,
        periodicityId2: undefined,
        criticality: 'MEDIUM',
        preAlertDays: undefined,
        preAlertHoursThreshold: undefined,
        active: true,
        componentId: undefined,
        subComponentId: initialSubComponentId,
        runningHourCounterId: undefined,
      })
      setErrors({})
      setSelectedEquipmentId(initialEquipmentId)
      if (initialEquipmentId) {
        loadSubcomponents(initialEquipmentId)
      } else {
        setSubcomponents([])
      }
    }
  }, [visible, planId, vesselId, initialEquipmentId, initialSubComponentId])

  useEffect(() => {
    if (selectedEquipmentId) {
      loadSubcomponents(selectedEquipmentId)
    } else {
      setSubcomponents([])
      setFormData((prev) => ({...prev, subComponentId: undefined}))
    }
  }, [selectedEquipmentId])

  useEffect(() => {
    const needsCounter =
      formData.scheduleType === 'RUNNING_HOURS' ||
      formData.scheduleType === 'HYBRID_WHICHEVER_FIRST' ||
      formData.scheduleType === 'HYBRID_BOTH_REQUIRED'
    if (needsCounter && selectedEquipmentId && vesselId) {
      loadRunningHourCounters()
    } else {
      setRunningHourCounters([])
    }
  }, [formData.scheduleType, selectedEquipmentId, vesselId])

  const loadEquipment = async () => {
    try {
      const data = await getEquipmentByVessel(vesselId)
      setEquipment(data)
    } catch (error) {
      console.error('Error loading equipment:', error)
      toast.error('Failed to load equipment')
    }
  }

  const loadSubcomponents = async (equipmentId: number) => {
    try {
      const data = await getSubcomponentsByEquipment(equipmentId)
      setSubcomponents(data)
    } catch (error) {
      console.error('Error loading subcomponents:', error)
    }
  }

  const loadRunningHourCounters = async () => {
    if (!vesselId || !selectedEquipmentId) return
    setLoadingCounters(true)
    try {
      const counters = await getRunningHourCounters(vesselId)
      // Filter counters by selected equipment
      const filtered = counters.filter((c) => c.equipmentId === selectedEquipmentId)
      setRunningHourCounters(filtered)
    } catch (error) {
      console.error('Error loading running hour counters:', error)
      toast.error('Failed to load running hour counters', {position: 'top-center'})
    } finally {
      setLoadingCounters(false)
    }
  }

  const getSelectedEquipmentName = () => {
    if (!selectedEquipmentId) return null
    const eq = equipment.find((e) => e.id === selectedEquipmentId)
    return eq ? eq.name : null
  }

  const getSelectedSubcomponentName = () => {
    if (!formData.subComponentId) return null
    const sub = subcomponents.find((s) => s.id === formData.subComponentId)
    return sub ? sub.name : null
  }

  const getPeriodicityOptions = (scheduleType?: PmsScheduleType) => {
    if (scheduleType === 'RUNNING_HOURS') return HOURS_PERIODICITY_TYPES
    if (scheduleType === 'TIME') return TIME_PERIODICITY_TYPES
    return HYBRID_PERIODICITY_TYPES
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const newErrors: {[key: string]: string} = {}

    if (!formData.taskDescription?.trim()) {
      newErrors.taskDescription = 'Job description is required'
    }
    if (!formData.jobType) {
      newErrors.jobType = 'Job type is required'
    }
    if (!formData.scheduleType) {
      newErrors.scheduleType = 'Schedule type is required'
    }
    const isHybrid =
      formData.scheduleType === 'HYBRID_WHICHEVER_FIRST' ||
      formData.scheduleType === 'HYBRID_BOTH_REQUIRED'
    const isTimeOnly = formData.scheduleType === 'TIME'
    const isHoursOnly = formData.scheduleType === 'RUNNING_HOURS'
    const timeTypes = ['M', 'W', 'D']
    const hybridTypes = ['M', 'W', 'D', 'H']

    if (isTimeOnly || isHoursOnly || isHybrid) {
      if (!formData.jobPeriodicity || formData.jobPeriodicity <= 0) {
        newErrors.jobPeriodicity = 'Periodicity value is required'
      }
    }

    const periodicityId = formData.periodicityId?.toUpperCase()
    if (isTimeOnly) {
      if (!periodicityId || !timeTypes.includes(periodicityId)) {
        newErrors.periodicityId = 'Periodicity type must be M (Months), W (Weeks), or D (Days)'
      }
    }

    if (isHoursOnly) {
      if (periodicityId !== 'H') {
        newErrors.periodicityId = 'Periodicity type must be H (Hours) for running hours schedule'
      }
    }

    if (isHybrid) {
      if (!periodicityId || !hybridTypes.includes(periodicityId)) {
        newErrors.periodicityId =
          'Periodicity type must be M (Months), W (Weeks), D (Days), or H (Hours)'
      }
    }

    const hasPeriodicity2Value = formData.jobPeriodicity2 != null && formData.jobPeriodicity2 !== 0
    const hasPeriodicity2Type = !!formData.periodicityId2?.trim()

    if (hasPeriodicity2Value || hasPeriodicity2Type) {
      if (!hasPeriodicity2Value) {
        newErrors.jobPeriodicity2 = 'Second periodicity value is required when type is selected'
      } else if ((formData.jobPeriodicity2 ?? 0) <= 0) {
        newErrors.jobPeriodicity2 = 'Second periodicity value must be greater than 0'
      }

      if (!hasPeriodicity2Type) {
        newErrors.periodicityId2 = 'Second periodicity type is required when value is selected'
      } else {
        const periodicityId2 = (formData.periodicityId2 ?? '').toUpperCase()
        if (isTimeOnly && !timeTypes.includes(periodicityId2)) {
          newErrors.periodicityId2 =
            'Second periodicity type must be M (Months), W (Weeks), or D (Days)'
        } else if (isHoursOnly && periodicityId2 !== 'H') {
          newErrors.periodicityId2 =
            'Second periodicity type must be H (Hours) for running hours schedule'
        } else if (isHybrid && !hybridTypes.includes(periodicityId2)) {
          newErrors.periodicityId2 =
            'Second periodicity type must be M (Months), W (Weeks), D (Days), or H (Hours)'
        }
      }
    }
    if (
      (formData.scheduleType === 'RUNNING_HOURS' ||
        formData.scheduleType === 'HYBRID_WHICHEVER_FIRST' ||
        formData.scheduleType === 'HYBRID_BOTH_REQUIRED') &&
      !formData.runningHourCounterId
    ) {
      newErrors.runningHourCounterId = 'Counter is required for running-hours schedules'
    }
    if (!selectedEquipmentId) {
      newErrors.equipmentId = 'Equipment is required'
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      return
    }

    setSubmitting(true)
    try {
      const dto: PmsPlanLineDto = {
        planId,
        taskDescription: formData.taskDescription!,
        jobInstructions: formData.jobInstructions?.trim() ? formData.jobInstructions.trim() : null,
        jobType: formData.jobType || 'I',
        workType: getWorkTypeFromJobType(formData.jobType),
        scheduleType: formData.scheduleType!,
        jobPeriodicity: formData.jobPeriodicity ?? null,
        periodicityId: formData.periodicityId ?? null,
        jobPeriodicity2: formData.jobPeriodicity2 ?? null,
        periodicityId2: formData.periodicityId2 ?? null,
        criticality: formData.criticality || 'MEDIUM',
        preAlertDays: formData.preAlertDays ?? null,
        preAlertHoursThreshold: formData.preAlertHoursThreshold ?? null,
        earlyTolerancePercent: formData.earlyTolerancePercent ?? null,
        earlyToleranceDays: formData.earlyToleranceDays ?? null,
        lateTolerancePercent: formData.lateTolerancePercent ?? null,
        lateToleranceDays: formData.lateToleranceDays ?? null,
        lateToleranceHours: formData.lateToleranceHours ?? null,
        active: formData.active ?? true,
        equipmentId: selectedEquipmentId ?? null,
        componentId: null,
        subComponentId: formData.subComponentId ?? null,
        runningHourCounterId:
          formData.scheduleType === 'RUNNING_HOURS' ||
          formData.scheduleType === 'HYBRID_WHICHEVER_FIRST' ||
          formData.scheduleType === 'HYBRID_BOTH_REQUIRED'
            ? formData.runningHourCounterId ?? null
            : null,
      }
      const createdLine = await createPmsPlanLine(planId, dto)
      toast.success('Periodicity created successfully', {position: 'top-center'})
      onSuccess(createdLine)
      onClose()
    } catch (error: any) {
      console.error('Error creating periodicity (plan line):', error)
      toast.error('Failed to create periodicity (plan line)', {
        position: 'top-center',
      })
    } finally {
      setSubmitting(false)
    }
  }

  if (!visible) return null

  return (
    <div
      style={WRAP_STYLE}
      tabIndex={-1}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        role='dialog'
        aria-modal='true'
        style={{
          width: 'min(1400px, 96vw)',
          height: 'min(900px, 92vh)',
          background: '#ffffff',
          borderRadius: '18px',
          boxShadow: '0 20px 60px rgba(15, 23, 42, 0.22)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            padding: '20px 24px',
            borderBottom: '1px solid #E5E7EB',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '16px',
            flexShrink: 0,
          }}
        >
          <div>
            <h5
              className='mb-1'
              style={{
                margin: 0,
                color: '#111827',
                fontSize: '1.2rem',
                fontWeight: 700,
              }}
            >
              Add Job Plan
            </h5>
            <div style={{color: '#6B7280', fontSize: '0.92rem'}}>
              Add a new periodicity to the selected plan.
            </div>
          </div>
          <button
            type='button'
            onClick={onClose}
            className='btn btn-sm'
            style={{
              border: '1px solid #D1D5DB',
              background: '#fff',
              color: '#374151',
              width: '38px',
              height: '38px',
              borderRadius: '10px',
              fontSize: '18px',
              lineHeight: 1,
            }}
          >
            ×
          </button>
        </div>
        <form
          onSubmit={handleSubmit}
          className='d-flex flex-column flex-grow-1'
          style={{minHeight: 0}}
        >
          <div
            className='flex-grow-1 overflow-auto px-4 py-3'
            style={{maxHeight: 'calc(92vh - 140px)'}}
          >
            {/* Equipment Selection */}
            <div className='mb-5'>
              <label className={LABEL}>Equipment</label>
              <SearchableSelect
                className={`form-select ${errors.equipmentId ? 'is-invalid' : ''}`}
                value={selectedEquipmentId || ''}
                onChange={(e) => {
                  const eqId = e.target.value ? Number(e.target.value) : undefined
                  setSelectedEquipmentId(eqId)
                  setFormData({...formData, subComponentId: undefined})
                }}
              >
                <option value=''>Select Equipment</option>
                {equipment.map((eq) => (
                  <option key={eq.id} value={eq.id}>
                    {eq.name} {eq.code && `(${eq.code})`}
                  </option>
                ))}
              </SearchableSelect>
              {errors.equipmentId && <div className='invalid-feedback'>{errors.equipmentId}</div>}
            </div>

            {/* Sub-Component Selection (optional) */}
            {selectedEquipmentId && (
              <div className='mb-5'>
                <label className={LABEL}>Sub-Component (Optional)</label>
                <SearchableSelect
                  className={`form-select ${errors.subComponentId ? 'is-invalid' : ''}`}
                  value={formData.subComponentId || ''}
                  onChange={(e) => {
                    setFormData({
                      ...formData,
                      subComponentId: e.target.value ? Number(e.target.value) : undefined,
                    })
                  }}
                >
                  <option value=''>Select Sub-Component (Optional)</option>
                  {subcomponents.map((sub) => (
                    <option key={sub.id} value={sub.id}>
                      {sub.name}
                    </option>
                  ))}
                </SearchableSelect>
                {errors.subComponentId && (
                  <div className='invalid-feedback'>{errors.subComponentId}</div>
                )}
              </div>
            )}

            {/* Hierarchy Path Display */}
            {(selectedEquipmentId || formData.subComponentId) && (
              <div className='mb-5'>
                <label className={LABEL}>Hierarchy Path</label>
                <div className='border rounded p-2 bg-light-subtle'>
                  <span className='text-muted'>Equipment</span>:{' '}
                  <strong>{getSelectedEquipmentName() || 'Not selected'}</strong>
                  {formData.subComponentId && (
                    <>
                      {' > '}
                      <span className='text-muted'>Sub-Component</span>:{' '}
                      <strong>{getSelectedSubcomponentName() || 'Not selected'}</strong>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Running Hour Counter Selector */}
            {(formData.scheduleType === 'RUNNING_HOURS' ||
              formData.scheduleType === 'HYBRID_WHICHEVER_FIRST' ||
              formData.scheduleType === 'HYBRID_BOTH_REQUIRED') &&
              selectedEquipmentId && (
                <div className='mb-5'>
                  <label className={LABEL}>
                    Running Hour Counter <span className='text-danger'>*</span>
                  </label>

                  {loadingCounters ? (
                    <div className='text-muted'>Loading counters...</div>
                  ) : runningHourCounters.length > 0 ? (
                    <>
                      <SearchableSelect
                        className={`form-select ${errors.runningHourCounterId ? 'is-invalid' : ''}`}
                        value={formData.runningHourCounterId ?? ''}
                        onChange={(e) => {
                          const v = e.target.value ? Number(e.target.value) : undefined
                          setFormData((prev) => ({...prev, runningHourCounterId: v}))
                          setErrors((prev) => ({...prev, runningHourCounterId: ''}))
                        }}
                      >
                        <option value=''>Select Counter</option>
                        {runningHourCounters.map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.name} (Current: {c.currentValue})
                          </option>
                        ))}
                      </SearchableSelect>
                      {errors.runningHourCounterId && (
                        <div className='invalid-feedback'>{errors.runningHourCounterId}</div>
                      )}

                      <div className='text-muted fs-7 mt-2'>
                        This counter will be used for due calculations and job generation.
                      </div>
                    </>
                  ) : (
                    <div className='border rounded p-2 bg-light-warning'>
                      <span className='text-warning'>
                        No running hour counters found for this equipment.
                      </span>
                      <div className='text-muted fs-7 mt-1'>
                        Create a counter in Running Hours first.
                      </div>
                    </div>
                  )}
                </div>
              )}

            {/* Job Description */}
            <div className='mb-5'>
              <label className={`${LABEL} required`}>Job Description</label>
              <textarea
                className={`form-control ${errors.taskDescription ? 'is-invalid' : ''}`}
                rows={3}
                value={formData.taskDescription || ''}
                onChange={(e) => setFormData({...formData, taskDescription: e.target.value})}
                placeholder='Enter job description...'
              />
              {errors.taskDescription && (
                <div className='invalid-feedback'>{errors.taskDescription}</div>
              )}
            </div>

            {/* Job Instructions */}
            <div className='mb-5'>
              <label className={LABEL}>Job Instructions</label>
              <textarea
                className='form-control'
                rows={3}
                value={formData.jobInstructions || ''}
                onChange={(e) => setFormData({...formData, jobInstructions: e.target.value})}
                placeholder='Detailed instructions for the job...'
              />
            </div>

            {/* Job Type */}
            <div className='mb-5'>
              <label className={`${LABEL} required`}>Job Type</label>
              <SearchableSelect
                className={`form-select ${errors.jobType ? 'is-invalid' : ''}`}
                value={formData.jobType || 'I'}
                onChange={(e) => {
                  const jobType = e.target.value
                  const workType = getWorkTypeFromJobType(jobType)
                  setFormData({...formData, jobType, workType})
                }}
              >
                {JOB_TYPES.map((jt) => (
                  <option key={jt.value} value={jt.value}>
                    {jt.label}
                  </option>
                ))}
              </SearchableSelect>
              {errors.jobType && <div className='invalid-feedback'>{errors.jobType}</div>}
            </div>

            {/* Schedule Type */}
            <div className='mb-5'>
              <label className={`${LABEL} required`}>Schedule Type</label>
              <SearchableSelect
                className={`form-select ${errors.scheduleType ? 'is-invalid' : ''}`}
                value={formData.scheduleType || ''}
                onChange={(e) => {
                  const st = e.target.value as PmsScheduleType
                  const isHybrid = st === 'HYBRID_WHICHEVER_FIRST' || st === 'HYBRID_BOTH_REQUIRED'
                  const needsTime = st === 'TIME' || isHybrid
                  const needsHours = st === 'RUNNING_HOURS' || isHybrid
                  setFormData((prev) => ({
                    ...prev,
                    scheduleType: st,
                    runningHourCounterId: needsHours ? prev.runningHourCounterId : undefined,
                  }))
                  if (!needsHours) {
                    setErrors((prev) => ({...prev, runningHourCounterId: ''}))
                  }
                }}
              >
                {SCHEDULE_TYPES.map((st) => (
                  <option key={st.value} value={st.value}>
                    {st.label}
                  </option>
                ))}
              </SearchableSelect>
              {errors.scheduleType && <div className='invalid-feedback'>{errors.scheduleType}</div>}
            </div>

            {/* Periodicities */}
            {(formData.scheduleType === 'TIME' ||
              formData.scheduleType === 'RUNNING_HOURS' ||
              formData.scheduleType === 'HYBRID_WHICHEVER_FIRST' ||
              formData.scheduleType === 'HYBRID_BOTH_REQUIRED') && (
              <div className='mb-5'>
                <label
                  className={`${LABEL} ${
                    formData.scheduleType === 'TIME' || formData.scheduleType === 'RUNNING_HOURS'
                      ? 'required'
                      : ''
                  }`}
                >
                  Periodicities
                </label>
                <div className='border rounded p-3 bg-light'>
                  <div className='row mb-3'>
                    <div className='col-md-6'>
                      <label className='form-label fs-7 text-muted mb-1'>
                        Periodicity 1 <span className='text-danger'>*</span>
                      </label>
                      <div className='row g-2'>
                        <div className='col-8'>
                          <input
                            type='number'
                            className={`form-control form-control-sm ${
                              errors.jobPeriodicity ? 'is-invalid' : ''
                            }`}
                            placeholder='Value'
                            min={1}
                            value={formData.jobPeriodicity ?? ''}
                            onChange={(e) =>
                              setFormData((prev) => ({
                                ...prev,
                                jobPeriodicity: e.target.value ? Number(e.target.value) : undefined,
                              }))
                            }
                          />
                          {errors.jobPeriodicity && (
                            <div className='invalid-feedback'>{errors.jobPeriodicity}</div>
                          )}
                        </div>
                        <div className='col-4'>
                          <SearchableSelect
                            className={`form-select form-select-sm ${
                              errors.periodicityId ? 'is-invalid' : ''
                            }`}
                            value={formData.periodicityId || ''}
                            onChange={(e) =>
                              setFormData((prev) => ({
                                ...prev,
                                periodicityId: e.target.value || undefined,
                              }))
                            }
                          >
                            <option value=''>Type</option>
                            {getPeriodicityOptions(formData.scheduleType).map((pt) => (
                              <option key={pt.value} value={pt.value}>
                                {pt.label}
                              </option>
                            ))}
                          </SearchableSelect>
                          {errors.periodicityId && (
                            <div className='invalid-feedback'>{errors.periodicityId}</div>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className='col-md-6'>
                      <label className='form-label fs-7 text-muted mb-1'>
                        Periodicity 2 (Optional)
                      </label>
                      <div className='row g-2'>
                        <div className='col-8'>
                          <input
                            type='number'
                            className={`form-control form-control-sm ${
                              errors.jobPeriodicity2 ? 'is-invalid' : ''
                            }`}
                            placeholder='Value'
                            min={1}
                            value={formData.jobPeriodicity2 ?? ''}
                            onChange={(e) =>
                              setFormData((prev) => ({
                                ...prev,
                                jobPeriodicity2: e.target.value
                                  ? Number(e.target.value)
                                  : undefined,
                              }))
                            }
                          />
                          {errors.jobPeriodicity2 && (
                            <div className='invalid-feedback'>{errors.jobPeriodicity2}</div>
                          )}
                        </div>
                        <div className='col-4'>
                          <SearchableSelect
                            className={`form-select form-select-sm ${
                              errors.periodicityId2 ? 'is-invalid' : ''
                            }`}
                            value={formData.periodicityId2 || ''}
                            onChange={(e) =>
                              setFormData((prev) => ({
                                ...prev,
                                periodicityId2: e.target.value || undefined,
                              }))
                            }
                          >
                            <option value=''>Type</option>
                            {getPeriodicityOptions(formData.scheduleType).map((pt) => (
                              <option key={pt.value} value={pt.value}>
                                {pt.label}
                              </option>
                            ))}
                          </SearchableSelect>
                          {errors.periodicityId2 && (
                            <div className='invalid-feedback'>{errors.periodicityId2}</div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className='text-muted fs-7'>
                    <i className='bi bi-info-circle me-1'></i>
                    {formData.scheduleType === 'TIME' &&
                      'Specify periodicity in Months (M), Weeks (W), or Days (D) for time-based scheduling.'}
                    {formData.scheduleType === 'RUNNING_HOURS' &&
                      'Specify periodicity in Hours (H) for running hours-based scheduling.'}
                    {(formData.scheduleType === 'HYBRID_WHICHEVER_FIRST' ||
                      formData.scheduleType === 'HYBRID_BOTH_REQUIRED') &&
                      'For hybrid schedules, use time-based types (M/W/D) and/or running hours (H). Periodicity 2 is optional. The system will use whichever comes first (or both if required).'}
                  </div>
                </div>
              </div>
            )}

            {/* Criticality */}
            <div className='mb-5'>
              <label className={LABEL}>Criticality</label>
              <SearchableSelect
                className='form-select'
                value={formData.criticality || 'MEDIUM'}
                onChange={(e) =>
                  setFormData({...formData, criticality: e.target.value as PmsCriticality})
                }
              >
                {CRITICALITY_LEVELS.map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </SearchableSelect>
            </div>

            {/* Active */}
            <div className='mb-5'>
              <div className='form-check'>
                <input
                  className='form-check-input'
                  type='checkbox'
                  checked={formData.active ?? true}
                  onChange={(e) => setFormData({...formData, active: e.target.checked})}
                  id='activeCheck'
                />
                <label className='form-check-label' htmlFor='activeCheck'>
                  Active
                </label>
              </div>
            </div>
          </div>
          <div className='border-top d-flex justify-content-end gap-2 px-4 py-3 flex-shrink-0'>
            <button
              type='button'
              className='btn btn-light btn-sm'
              onClick={onClose}
              disabled={submitting}
            >
              Cancel
            </button>
            <button type='submit' className='btn btn-primary btn-sm' disabled={submitting}>
              {submitting ? 'Creating...' : 'Create Periodicity (Plan Line)'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
